/**
 * Routes whose POST/PUT/DELETE must NOT raise the generic toast, matched by
 * `matchesToastRoute` below — an entry means the route it NAMES, plus anything
 * under it, and nothing else.
 *
 * Five entries are deliberately OPEN-ENDED (trailing `*`): they were written as
 * loose substrings and real endpoints rely on that reach today, so they are left
 * exactly as they were rather than re-pointed at a guess. Each says what it is
 * standing in for; narrowing them is a separate, per-route decision.
 */
export const ON_POST_PUT_URLS_TOAST_CONDITIONS: {
  [url: string]: NotificationCondition;
} = {
  ['/session-logout']: { canShow: false, canShowProgress: false },
  ['/preferences/property']: { canShow: false, canShowProgress: false },
  ['/preferences/theme']: { canShow: false, canShowProgress: false },
  ['/preferences/layouts']: { canShow: false, canShowProgress: false },
  // Open-ended: besides the many `…/search` endpoints this also silences the
  // refinitiv `…/instruments/refinitiv/search-equity|-gov-corp|-futures-and-options|-money`
  // lookups and `/meta-info/view-columns/search-view-columns`.
  ['/search*']: { canShow: false, canShowProgress: false },
  // Open-ended: `/auth/cookie` — but also every `…/authorized-persons` write.
  ['/auth*']: { canShow: false, canShowProgress: false },
  ['/client-valuation-reports']: { canShow: false, canShowProgress: false },
  ['/orders/check']: { canShow: false, canShowProgress: false },
  ['/assign-submitter']: { canShow: true, canShowProgress: false },
  ['/orders/submit']: { canShow: true, canShowProgress: false },
  ['/in-progress']: { canShow: true, canShowProgress: false },
  ['/orders/brokerage-rate']: { canShow: false, canShowProgress: false },
  ['/orders/multiple-brokerage-rate']: { canShow: false, canShowProgress: false },
  // The instrument dialog starts the standard market-data load by itself and shows its own
  // progress/result inline (InstrumentMarketDataLoadService): no generic toast.
  ['/market-data/load']: { canShow: false, canShowProgress: false },
  ['/push-notification-subscriptions']: { canShow: false, canShowProgress: false },
  // Open-ended: the real writes are `/push-notifications/:id/open|close`.
  ['/push-notification*']: { canShow: false, canShowProgress: false },
  // Open-ended: nothing writes to `/user` itself — this covers `/users/password`
  // and `/user-bank-account-group`.
  ['/user*']: { canShow: false, canShowProgress: false },
  // Open-ended: a truncated name, kept as written. It covers the one real write,
  // POST `/pdf-reports/pdf-report`.
  ['/pdf-repor*']: { canShow: true, canShowProgress: false },
  ['/meta-info/current-view']: { canShow: false, canShowProgress: false },
  ['/meta-info/views/view-columns-by-search']: { canShow: false, canShowProgress: false },
  ['/meta-info/view-columns/view-column-by-search']: { canShow: false, canShowProgress: false },
  ['/meta-info/entities/entity-field']: { canShow: false, canShowProgress: false },
  ['/instruments-by-ids']: { canShow: false, canShowProgress: false },
  ['/risk-report']: { canShow: false, canShowProgress: false },
  ['/twelvedata']: { canShow: false, canShowProgress: false },
  ['/recommendations']: { canShow: false, canShowProgress: false },
  ['/exante/security-list']: { canShow: false, canShowProgress: false },
  ['/gtn-security-list']: { canShow: false, canShowProgress: false },
  ['/funding-costs']: { canShow: false, canShowProgress: false },
  ['/processes/']: { canShow: false, canShowProgress: false },
  // Marketplace funnel analytics: fire-and-forget batches the user never asked for.
  ['/product-views/events']: { canShow: false, canShowProgress: false },
  // Backup Manager writes already toast from their own dialogs (export / schedule /
  // AWS settings). The interceptor otherwise stacks a second generic message on
  // POST /backup/manual-export — the queued-export case in particular.
  ['/backup/']: { canShow: false, canShowProgress: false },
  // banks-custom data-mapping helpers: background parse + unsaved preview POSTs persist nothing,
  // so they must not raise the "Data sent and saved successfully" toast.
  ['/raw-positions/parse']: { canShow: false, canShowProgress: false },
  ['/raw-transactions/parse']: { canShow: false, canShowProgress: false },
  ['/positions/preview']: { canShow: false, canShowProgress: false },
  ['/transactions/preview']: { canShow: false, canShowProgress: false },
  // Rule testing only returns matching source filenames; it does not persist the rule.
  ['/file-fetching-settings/test']: { canShow: false, canShowProgress: false },
  // Polled every 2s by GetCurrencyPairPricePipe for GTN — a read, not a write, and the
  // generic toast would otherwise fire on every poll tick.
  ['/gtn-institutions-currency-rates/fetch']: { canShow: false, canShowProgress: false },
  // POST /runs (agent runtime): a refused launch is ALREADY toasted by the run
  // viewer with the runtime's own reason ("Could not start the run" + the
  // verbatim 403/409/429 detail, plus where to fix it). The global interceptor
  // fired "Something went wrong" on top, so ONE refusal produced TWO toasts —
  // the specific one saying what to do and a vague one implying a second,
  // unrelated failure. A started run is likewise self-evident (it appears in the
  // run list and opens), so the generic success toast goes with it.
  ['/runs']: { canShow: false, canShowProgress: false },
  // POST /approvals/:id (agent runtime): the SAME double toast as POST /runs, on
  // the more consequential action. The approvals queue already toasts what
  // actually happened — the decision by name once the runtime accepted it, and
  // on a refusal the runtime's own cause (the maker-checker 409 above all,
  // which the generic "Something went wrong" actively obscures).
  ['/approvals']: { canShow: false, canShowProgress: false },
  // accounts `/passkeys/**`. Every write here is already spoken for by the UI that
  // starts it, and the generic toast contradicted all three:
  //  - POST `register/options` is a step, not a result. Its normal refusal is "the
  //    session is no longer fresh, type your password", which the dialog answers by
  //    revealing the field — with a red "Something went wrong" stacked on top, saying
  //    a failure happened when nothing had. Its success is options, not a passkey, so
  //    "Data sent and saved successfully" arrived before anything was saved.
  //  - POST `register` is the real creation, and the caller toasts it by name
  //    ("Passkey added") once the key exists.
  //  - DELETE `/passkeys/:id` is toasted by the passkeys tab with its own wording for
  //    both outcomes, so the interceptor was simply doubling it.
  // Failures stay visible either way: the dialogs show the server's own description
  // above the password field, and the tab toasts its own removal error.
  ['/passkeys']: { canShow: false, canShowProgress: false },
};

export interface NotificationCondition {
  canShow: boolean;
  canShowProgress: boolean;
}

/** Where a route entry is allowed to end: end of URL, or a path/query/fragment break. */
const ROUTE_END_CHARS = ['', '/', '?', '#'];

/**
 * Does `url` hit the route this `entry` NAMES?
 *
 * The request URL carries whatever API base path the environment is on, so the
 * match cannot be an equality check. It used to be a bare `url.includes(entry)`,
 * which quietly handed an entry every LONGER name containing it: `/runs` would
 * also have silenced a future `/runsheets`, and a silently missing error toast
 * is the exact failure this list is meant to prevent.
 *
 * An entry now matches only where it ends on a path boundary, so `/runs` matches
 * `/api/agent-runtime/runs`, `/runs?x=1` and `/runs/:id`, but not `/runsheets`.
 * Two forms stay open-ended: a trailing `/` ("this route and everything under
 * it", e.g. `/processes/`) and a trailing `*` ("this name and anything appended
 * to it", for the entries flagged above).
 */
export function matchesToastRoute(url: string, entry: string): boolean {
  const openEnded = entry.endsWith('*');
  const needle = openEnded ? entry.slice(0, -1) : entry;

  if (openEnded || needle.endsWith('/')) {
    return url.includes(needle);
  }

  // Every occurrence, not just the first: `/pdf-reports/pdf-report` ends on a
  // boundary only at its second `/pdf-report`.
  for (let at = url.indexOf(needle); at !== -1; at = url.indexOf(needle, at + 1)) {
    if (ROUTE_END_CHARS.includes(url.charAt(at + needle.length))) {
      return true;
    }
  }

  return false;
}
